import { CommonModule } from '@angular/common';
import { Component, inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { MarkdownModule } from "ngx-markdown";
import { DialogModule } from 'primeng/dialog';
import { TooltipModule } from 'primeng/tooltip';
import { Observable, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { UserNameDisplayComponent } from 'src/app/@shared/components/user-name-display';
import { AssetUrlPipe } from 'src/app/@shared/pipes/asset-url.pipe';
import { ButtonComponent } from '../../../@core/components/ui/button/button.component';
import { CheckboxComponent } from '../../../@core/components/ui/checkbox/checkbox.component';
import { PopoverComponent } from '../../../@core/components/ui/popover/popover.component';
import { FILE_TYPE } from '../../../@core/models/enums';
import { ILinkDocument, IUser } from '../../../@core/models/model';
import { AuthenticationService } from '../../../@core/services/authentication.service';
import { ToastService } from '../../../@core/services/toast.service';
import { AiAssistantComponent } from "../../../@shared/components/ai-assistant/ai-assistant.component";
import { BadgeComponent } from '../../../@shared/components/badge/badge.component';
import { FileIconComponent } from '../../../@shared/components/file-icon/file-icon.component';
import { FileSelectionComponent } from '../../../@shared/components/file-selection/file-selection.component';
import { FetchFunction, FileItem, FileSelectionChangeEvent, FileSelectionConfig, FileSelectionSubmitEvent } from '../../../@shared/components/file-selection/file-selection.model';
import { DateFormatterPipe } from '../../../@shared/pipes/dateFormater.pipe';
import { CONTRACT_TYPE, IContract } from '../../models/contract-management.model';
import { ContractService } from '../../services/contracts.service';
import { LinkDocumentService } from '../../services/link-document.service';
import { CreateContractComponent } from '../create-contract/create-contract.component';

@Component({
  selector: 'app-link-file',
  standalone: true,
  imports: [ButtonComponent, DialogModule, ReactiveFormsModule, FormsModule, FileSelectionComponent, CommonModule, PopoverComponent, BadgeComponent, MarkdownModule, AssetUrlPipe, UserNameDisplayComponent, AiAssistantComponent, CheckboxComponent, FileIconComponent, DateFormatterPipe, TooltipModule],
  templateUrl: './link-file.component.html',
  styleUrl: './link-file.component.scss'
})

export class LinkFileComponent implements OnInit, OnDestroy {
  @Input() document!: IContract;
  @Input() projectId!: string;

  currentUser: IUser | null | undefined;
  documents: string[] | undefined = [];
  selectedDocument: string | undefined;
  linkDocs: ILinkDocument[] = [];

  isExistingDocOpen = false;
  isCreateDocOpen = false;

  dialog = inject(MatDialog)

  reloadLinkedFilesSubscription!: Subscription;

  fileSelectionConfig: FileSelectionConfig = {
    title: 'Link Existing Document',
    submitButtonLabel: 'Link Selected',
    showSearch: true,
    searchPlaceholder: 'Search Documents',
    defaultPageSize: 50,
    selectedPanelTitle: 'Selected Documents',
    selectedEmptyMessage: 'Select documents to add them'
  };

  fileSelectionFetch: FetchFunction = (event: FileSelectionChangeEvent): Observable<{ items: FileItem[]; totalRecords: number }> => {
    const excludeDocs: string[] = (this.linkDocs || []).map(linkDoc => linkDoc.targetDocument._id);
    excludeDocs.push(this.document._id);

    const skip = event.page * event.pageSize;
    return this.contractService.getContracts({
      type: CONTRACT_TYPE.ALL,
      skip,
      limit: event.pageSize,
      name: event.searchQuery || '',
      excludeDocs,
    }).pipe(
      map((res) => {
        if (res && res.data) {
          const items: FileItem[] = res.data.contracts.map((contract: IContract) => ({
            id: contract._id,
            name: contract.name,
            metadata: { originalContract: contract },
          }));
          return { items, totalRecords: res.data.total ?? items.length };
        }
        return { items: [], totalRecords: 0 };
      })
    );
  };

  isLoading: boolean = true

  constructor(
    private linkDocumentService: LinkDocumentService,
    private contractService: ContractService,
    private toast: ToastService,
    private authService: AuthenticationService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.authService.currentUser.subscribe(res => {
      this.currentUser = res;
    });
    this.route.queryParamMap.subscribe(params => {
      this.projectId = params.get('projectId') as string ?? '';
    });
  }

  ngOnInit() {
    this.reloadLinkedFilesSubscription = this.linkDocumentService.reloadLinkedFiles.subscribe(res => {
      this.fetchLinkDocs();
    })
    this.fetchLinkDocs();
  }

  ngOnDestroy() {
    if (this.reloadLinkedFilesSubscription) {
      this.reloadLinkedFilesSubscription.unsubscribe()
    }
  }

  openExistingDoc() {
    this.isExistingDocOpen = true;
  }

  resetExistingDoc() {
    this.isExistingDocOpen = false;
  }

  fetchLinkDocs() {
    this.isLoading = true;
    this.linkDocumentService.fetchLinkDocument(this.document._id).subscribe(res => {
      if (res && res.data) {
        this.linkDocs = res.data.data;
        this.isLoading = false;
      } else {
        this.toast.error('Error fetching linked documents');
      }
    });
  }

  onFileSelectionSubmit(event: FileSelectionSubmitEvent) {
    const selectedDocs = (event.allSelectedFiles ?? [])
      .map(file => file.id)
      .filter(id => id !== this.document._id && !this.linkDocs.some(linkDoc => linkDoc.targetDocument._id === id));
    this.linkDocumentService.linkDocument(this.document._id, selectedDocs, this.currentUser?._id).subscribe(res => {
      if (res && res.success) {
        this.toast.success('Documents Linked successfully');
        this.resetExistingDoc();
        this.fetchLinkDocs();
        this.linkDocumentService.reloadLinkedFiles.next(this.document._id);
      } else {
        this.resetExistingDoc();
      }
    });
  }

  getFileType(fileName: string): FILE_TYPE | undefined {
    const ext = fileName.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'pdf': return FILE_TYPE.PDF;
      case 'doc':
      case 'docx': return FILE_TYPE.WORD;
      case 'xls':
      case 'xlsx': return FILE_TYPE.EXCEL;
      case 'ppt':
      case 'pptx': return FILE_TYPE.POWERPOINT;
      case 'html': return FILE_TYPE.HTML;
      case 'txt': return FILE_TYPE.TEXT;
      case 'csv': return FILE_TYPE.CSV;
      case 'md': return FILE_TYPE.MARKDOWN;
      case 'python': return FILE_TYPE.PYTHON;
      case 'json': return FILE_TYPE.JSON;
      case 'jpg': return FILE_TYPE.JPG;
      case 'png': return FILE_TYPE.PNG;
      case 'jpeg': return FILE_TYPE.JPEG;
      default: return undefined;
    }
  }

  handleMenuItemClick(event: Event, documentId: string, item: { label: string, icon: string, route: string[] }) {
    event.stopPropagation();
    if (item.label === 'Unlink') {
      this.deleteLinkDocument(documentId);
    }
  }

  deleteLinkDocument(documentId: string) {
    this.linkDocumentService.deleteLinkDocument(documentId).subscribe(res => {
      if (res && res.success) {
        this.toast.success('Unlinked document successfully');
        this.fetchLinkDocs();
        this.linkDocumentService.reloadLinkedFiles.next(this.document._id);
      }
    });
  }

  async openDocument(documentId: string) {
    const url = this.router.createUrlTree(['edit-document'], {
      queryParams: { documentId, ...(this.projectId ? { projectId: this.projectId } : {}) },
    }).toString();

    window.open(url, '_blank');
  }

  openCreateDoc() {
    this.isCreateDocOpen = true;
  }

  openAndLinkDoc({ docTypeId, documentId }: { docTypeId: string, documentId?: any }) {
    this.linkDocumentService.linkDocument(this.document._id, [documentId], this.currentUser?._id).subscribe(res => {
      if (res && res.success) {
        this.isCreateDocOpen = false;
        this.linkDocumentService.reloadLinkedFiles.next(this.document._id);
        this.openDocument(documentId)
      } else {
        this.isCreateDocOpen = false;
      }
    });
  }

  closeDialog({ docTypeId, documentId }: { docTypeId: string, documentId?: any }) {
    this.isCreateDocOpen = false;
  }

  handleDialogClose() {
    this.resetExistingDoc();
  }

  openUploadDoc() {
    const _dialogRef = this.dialog.open(CreateContractComponent, {
      disableClose: true,
      minWidth: '40rem',
      data: { returnUploadedDoc: true, allowMultiple: true }
    });

    _dialogRef.afterClosed().subscribe((response: File[]) => {
      if (response) {
        let files = []
        if (Array.isArray(response)) {
          files = response
        } else {
          files = [response]
        }
        this.isLoading = true
        this.linkDocumentService.uploadAndLinkDocument({ files, document: this.document._id }).subscribe(res => {
          if (res && res.success) {
            this.isLoading = false
            this.toast.success('Document uploaded and linked successfully');
            this.fetchLinkDocs();
            this.linkDocumentService.reloadLinkedFiles.next(this.document._id);
          }
        });
      }
    })

  }
}
